import   React               ,
       { useState            ,
         useEffect         }  from 'react';

import   classNames             from 'classnames';

import   styles                 from './BroadcastingChannelCategoryList.module.scss';

import   BroadcastingChannelCategoryRow from './BroadcastingChannelCategoryRow';
import   Window                 from '../../widget/windows/window';
import   ColumnTitle            from '../../widget/dataDisplay/labels/ColumnTitle'

import { useTypedSelector    }  from '../../../store/useTypeSelector';
import { RootState           }  from '../../../store/generalReducer';

import { BroadcastingChannelCategory       }  from '../../../dataTypes/broadcastingManagment/types';
import { getBroadcastingChannelCategories  ,
         patchBroadcastingChannelCategory  ,
         deleteBroadcastingChannelCategory }  from '../../../backendServices/broadcastingManagment/api_category';
import { DUPLICATE_NAME_CODE }  from '../../../dataTypes/backendServicesManagment/types';

interface BroadcastingChannelCategoryListProps {
  refresh              : boolean;
  onCategoriesFetched? : (count: number) => void;
  onDelete?            : ()=> void;
 }

export const BroadcastingChannelCategoryList = ({ refresh             ,
                                                  onCategoriesFetched ,
									              onDelete            }: BroadcastingChannelCategoryListProps) => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);

  const [ categorySet      , setCategorySet      ] = useState<BroadcastingChannelCategory[]>([]);
  const [ showConfirmation , setShowConfirmation ] = useState(false);
  const [ categoryToDelete , setCategoryToDelete ] = useState<BroadcastingChannelCategory | null>(null);
  const [ showWindow       , setShowWindow       ] = useState(false);
  const [ errorMessage     , setErrorMessage     ] = useState<JSX.Element>(<></>);
  const [ successMessage   , setSuccessMessage   ] = useState<JSX.Element>(<></>);
  const [ isSuccess        , setIsSuccess        ] = useState(false);

  useEffect(() => {
    (async () => {
      const result = await getBroadcastingChannelCategories(authentication.admin?.id);

      if (result.payload)
       {
        const sortedCategories = result.payload.sort((a, b) => a.rank - b.rank);

        setCategorySet(sortedCategories);

        if (onCategoriesFetched) {
          onCategoriesFetched(sortedCategories.length);
        }
       }

    })();
  }, [refresh, onCategoriesFetched, authentication.admin?.id]);

  const makeUpdate = async (category: BroadcastingChannelCategory) => {
    const { id, name, rank, visible } = category;
    return patchBroadcastingChannelCategory(id, name, rank, visible, authentication.admin?.id);
  };

  const handleNameChange = async ( index        : number , 
                                   newName      : string ) => {
    const previousCategories = categorySet;

    const updatedCategories = [...categorySet];
    updatedCategories[index] = {
      ...updatedCategories[index],
      name: newName
    };
    setCategorySet(updatedCategories);

    const result = await makeUpdate(updatedCategories[index]);

    if (result.code === 1) { return; }

    // Le refus arrive APRÈS que la liste a pris le nouveau nom : elle est modifiée
    // sans attendre la réponse, pour que la saisie reste fluide. On la remet donc
    // dans son état d'avant, sinon l'écran continuerait d'afficher un nom que le
    // serveur n'a pas retenu — et la ligne, qui se resynchronise sur sa prop, revient
    // avec elle.
    setCategorySet(previousCategories);
    setErrorMessage(result.code === DUPLICATE_NAME_CODE
                    ? <>Cette catégorie existe déjà</>
                    : <>Une erreur est survenue lors du renommage de la catégorie.</>);
    setIsSuccess(false);
    setShowWindow(true);
  }

  const handleArrowClick = (index: number, direction: 'up' | 'down') => {
    const updatedCategories = [...categorySet];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;

    if (swapIndex >= 0 && swapIndex < updatedCategories.length) 
	 {
      [ updatedCategories[index]     , updatedCategories[swapIndex] ] = 
	  [ updatedCategories[swapIndex] , updatedCategories[index]     ];
      
	  [ updatedCategories[index].rank     , updatedCategories[swapIndex].rank ] = 
	  [ updatedCategories[swapIndex].rank , updatedCategories[index].rank     ];

      setCategorySet(updatedCategories);
      makeUpdate(updatedCategories[index]);
      makeUpdate(updatedCategories[swapIndex]);
     }
  };

  const handleDeleteClick = (category: BroadcastingChannelCategory) => {
    setCategoryToDelete(category);
    setShowConfirmation(true);
  };

  const handleDeleteConfirmation = async (confirmed: boolean) => {
    if (confirmed && categoryToDelete) 
	 {
      const result = await deleteBroadcastingChannelCategory(categoryToDelete.id, authentication.admin?.id);

      if (result.code === 1) 
	        {
             setSuccessMessage ( <>{`La suppression de la catégorie ${categoryToDelete.name} a été effectuée.`}</> );
          // setCategorySet((prev) => prev.filter((c) => c.id !== categoryToDelete.id));
             if (onDelete)
              {
			   onDelete()
			  }
	        } 
	   else {
             setErrorMessage( <>{`Une erreur est survenue lors de la suppression de la catégorie ${categoryToDelete.name}.`}</> );
            }

      setIsSuccess(result.code === 1);
     }
	 
    setShowConfirmation(false);
    setCategoryToDelete(null);
    setShowWindow(true);
  };

  const handleVisibilityChange = (index: number, newState: boolean) => {
    const updatedCategories = [...categorySet];
    updatedCategories[index] = {
      ...updatedCategories[index],
      visible: newState,
    };
    setCategorySet(updatedCategories);
    makeUpdate(updatedCategories[index]);
  };

  const handleCloseWindow = () => {
    setShowWindow(false);
  };

  return (
    <div className={classNames(styles.root)}>
	
<div className={classNames(styles.sectionHeader)}>
  <div className={classNames(styles.sectionIcon)}>
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <circle cx="3" cy="3"  r="1.4" fill="#3b82f6" />
      <rect   x="7" y="1.8" width="13" height="2.4" rx="1.2" fill="#3b82f6" />
      <circle cx="3" cy="9"  r="1.4" fill="#3b82f6" />
      <rect   x="7" y="7.8" width="13" height="2.4" rx="1.2" fill="#3b82f6" />
      <circle cx="3" cy="15" r="1.4" fill="#3b82f6" />
      <rect   x="7" y="13.8" width="13" height="2.4" rx="1.2" fill="#3b82f6" />
      <circle cx="3" cy="21" r="1.4" fill="#3b82f6" />
      <rect   x="7" y="19.8" width="13" height="2.4" rx="1.2" fill="#3b82f6" />
    </svg>
  </div>
  <span className={classNames(styles.sectionTitle)}>Liste des rubriques</span>
</div>

      <div className={classNames(styles.container)}>
        <ColumnTitle titles = {[ 'Rang' , 'Titre' , ''     , 'Actions' ,''      , 'Visibilité' ]}
                     widths = {[ '50px' , 'auto'  , '50px' , '100px'   , '50px' , '100px'     ]} />

        {
         categorySet.map ((category, index) => (
          <BroadcastingChannelCategoryRow key                      = {category.id}
                            category                 = {category}
                            categorySetLenght        = {categorySet.length}
                            onNameChange             = {handleNameChange}
                            onArrowClick             = {handleArrowClick}
                            onDeleteClick            = {handleDeleteClick}
                            onVisibilityChange       = {handleVisibilityChange} />
         )
        )
       }
      </div>

      {
       showConfirmation &&
        (
         <Window message                 = {<>{`Êtes-vous sûr de vouloir supprimer la catégorie ${categoryToDelete?.name} ?`}</>}
                 messageType             = "question"
                 onClickOnValidateButton = {() => handleDeleteConfirmation(true)}
                 onClickOnCancelButton   = {() => handleDeleteConfirmation(false)} />
        )
      }

      {
       showWindow &&
        (
         <Window message                 = {isSuccess ? successMessage : errorMessage}
                 messageImportance       = {isSuccess ? 'normal' : 'important'}
                 onClickOnValidateButton = {handleCloseWindow} />
        )
      }
    </div>
  );
};

export default BroadcastingChannelCategoryList;
